import { FaceRecognitionStatusEnum, LayoutEnum } from '@goldcast/api/content';
import type { CSSProperties } from './css-properties';
import { CaptionPresetName } from '../caption-presets/types';
import { CaptionTransitions, SpeakerLabelTransitions } from './transitions';
import { FontSizePreset } from './speaker-label-font-size';
import { Word } from './transcript-types';

export enum CaptionsPlacement {
  Top = 'Top',
  Middle = 'Middle',
  Bottom = 'Bottom'
}
export enum HighlightTypeEnum {
  None = 'none',
  Text = 'text',
  Box = 'box'
}
export enum CaptionStyle {
  Basic = 'Basic',
  Outline = 'Outline',
  Background = 'Background'
}
export enum CaptionLineConfig {
  Default = 'Default',
  OneWord = '1 Word',
  OneLine = '1 Line',
  TwoLines = '2 Lines',
  ThreeLines = '3 Lines'
}
export enum ClipPublishedStatus {
  PUBLISHED = 'PUBLISHED',
  DRAFT = 'DRAFT',
  PROCESSING = 'PROCESSING',
  FAILED = 'FAILED'
} // TODO: Temporary solution till we find a way to export it from BE

export type SizeType = 'LANDSCAPE' | 'PORTRAIT' | 'SQUARE';
export type HighlightType = 'none' | 'text' | 'box';
export type FitType = 'FIT' | 'FILL';
export type DeleteType = 'FILLER_WORDS_MENU' | 'MANUAL' | 'AI_EDITS' | 'PAUSE_OPTIMIZATION';
export type SourceType = 'AI' | 'USER';
export type TextType = 'BLOG' | 'CUSTOM' | 'EMAIL' | 'LINKEDIN' | 'TAKEAWAYS';
export type LayoutType = LayoutEnum;
export enum LayoutDisableReason {
  AUDIO_CONTENT_NOT_SUPPORTED = 'Not supported for Audio Content.',
  GRID_NOT_SUPPORTED_FOR_SINGLE_SPEAKER = 'The Grid layout works with multiple speakers.',
  FOCUS_NOT_SUPPORTED_FOR_SINGLE_SPEAKER = 'The Focus layout works with multiple speakers or screen shares.',
  PIP_NOT_SUPPORTED_FOR_SINGLE_SPEAKER = 'The Picture in Picture layout works with multiple speakers or screen shares.',
  FIT_FILL_NOT_SUPPORTED_FOR_AUDIOGRAM = 'Not supported for Audiogram.',
  FILL_NOT_SUPPORTED_FOR_ORIGINAL_LAYOUT = 'Fill option not supported for Original Layout.',
  FIT_NOT_SUPPORTED_FOR_UPLOADS_WITH_SPEAKER_LAYOUT = 'Fit option not supported for Uploads with Speaker Layout.',
  SPEAKER_IDENTIFICATION_IN_PROGRESS = 'This layout will be available once the speakers have been identified.',
  SPEAKER_IDENTIFICATION_FAILED = "Speaker layouts aren't supported for this video — speaker faces couldn't be detected.",
  CLIP_EXCEEDS_MAX_DURATION = 'This layout is not supported for clips longer than 3 minutes.',
  UNMIGRATED_LIVE_EVENT_DOESNT_SUPPORT_SPEAKER_LAYOUTS = 'Migration is required to use this layout.',
  SPLITS_ENABLED_WITHOUT_FACE_REC = "Speaker layouts aren't supported for this video."
}
export type FitMode = 'FIT' | 'FILL';
export enum RecordingVideoType {
  SCREENSHARE = 'SCREENSHARE',
  USER_FEED = 'USER_FEED',
  SHARED_VIDEO = 'SHARED_VIDEO'
}
export interface CaptionStyles {
  caps: boolean;
  placement?: CaptionsPlacement;
  font_size: number;
  line_config: CaptionLineConfig;

  /**
   * @deprecated
   */
  scale: number;

  /**
   * @deprecated
   */
  animation: CaptionStyle;

  /**
   * @deprecated
   */
  background_style_color?: string;

  font_location?: string;
  theme_preset?: CaptionPresetName | 'custom';
  pageStyle?: Omit<CSSProperties, 'backgroundColor'> & {
    /**
     * @deprecated Using 'backgroundColor' in pageStyle is discouraged. Use 'background' instead.
     */
    backgroundColor?: never;
  };
  wordStyle?: {
    active?: CSSProperties;
    inactive?: CSSProperties;
    highlightType?: HighlightTypeEnum;
    highlightBoxStyle?: CSSProperties;
  };
  letterStyle?: CSSProperties;
  transitions?: CaptionTransitions;
}
export interface LayoutStatus {
  status: FaceRecognitionStatusEnum;
  reason: string;
  accurate: boolean;
}

export interface CaptionsPosition {
  x: number;
  y: number;
  width: number;
  height: number;
  textX?: number;
  textY?: number;
}
export interface ScreenshareDetectionStatus {
  status: 'DONE' | 'FAILED' | 'UNAVAILABLE';
}
export interface SpeakerLabelTextStyle extends CSSProperties {
  fontSizePreset?: FontSizePreset;
}

export interface SpeakerLabelStyles {
  font_location: string;
  title_font_location?: string;
  text_color: string;
  background_color: string;
  border_radius: string;
  containerStyle?: CSSProperties;
  nameStyle?: SpeakerLabelTextStyle;
  titleStyle?: SpeakerLabelTextStyle;
  transitions?: SpeakerLabelTransitions;
}
export interface AssetConfigCaptionStyle {
  highlight_style?: AssetConfigCaptionStyleItem;
  regular_style?: AssetConfigCaptionStyleItem;
}

export interface AssetConfigCaptionStyleItem {
  background_color?: string;
  font?: string;
  font_color?: string;
  font_size?: number;
  font_location?: string;
  font_opacity?: string;
  scale_y?: number;
  scale_x?: number;
  border_style?: number;
  outline?: number;
  shadow?: number;
  bold?: number;
  alignment?: number;
}
export interface AssetConfig {
  canvas_width: number;
  canvas_height: number;
  duration: number;
  background_color: string;
  background_image?: string;
  filename: string;
  videos: AssetConfigVideo[];
  captions: AssetConfigCaption[];
  isAnalysisAccurate?: boolean;
  remotion_captions?: boolean;
}

export interface AssetConfigVideo {
  start: number;
  end: number;
  x: number;
  y: number;
  z: number;
  bounding_width: number;
  bounding_height: number;
  radius?: number;
  meta: {
    source?: string;
    in_time: number;
    out_time?: number;
    excludes?: any[];
    crop_x: number;
    crop_y: number;
    crop_width: number;
    crop_height: number;
  };
}

interface AssetConfigCaption {
  style?: AssetConfigCaptionStyle;
  words?: AssetConfigCaptionWord[] | Word[];
  left_margin?: number;
  right_margin?: number;
  bottom_margin?: number;
  rotation?: number;
  z?: number;
  drift?: number;
}

interface AssetConfigCaptionWord {
  content: string;
  processed_start_time: number;
  processed_end_time: number;
  srt_index: number;
  hidden: true;
  speaker_label: string;
}
